/**
 * inputState.ts
 * Shared mutable input flags for the Starfall Courier game loop.
 *
 * Flags are written by:
 *   - DPad.tsx and ActionButtons.tsx (gesture handlers — native + web)
 *   - KeyboardInput.tsx (keyboard shim — web dev only)
 * and read by the game loop each tick. This module is the single source of
 * truth for current frame input.
 *
 * IMPORTANT: Do not add React hooks or async calls to this file.
 * This module is imported by engine files which must remain hook-free.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1, src/game/input/ folder.
 *
 * @module inputState
 */

import type { InputState } from '../types/input';

/**
 * The single mutable input state instance.
 * Written by gesture handlers; read each physics tick by the game loop.
 * All flags default to false (no input).
 */
export const inputState: InputState = {
  left: false,
  right: false,
  down: false,
  up: false,
  jumpPressed: false,
  jumpHeld: false,
  attackPressed: false,
  attackHeld: false,
  dashPressed: false,
  interactPressed: false,
};

/**
 * Reset all rising-edge (one-frame) flags to false.
 * Call at the END of each physics tick, after the game loop has consumed them.
 * Held flags (jumpHeld, left, right) are NOT reset here — they persist
 * until the gesture handler releases them.
 */
export function clearRisingEdgeFlags(): void {
  inputState.jumpPressed = false;
  inputState.attackPressed = false;
  inputState.dashPressed = false;
  inputState.interactPressed = false;
}

/**
 * Clear ALL input flags (held + rising-edge) to their default false state.
 * Use on pause, navigation, or session teardown to prevent latched input
 * from persisting across game sessions.
 *
 * DECISION: This function is in inputState.ts (no React) so engine-layer
 * code (PlayScene) can call it without importing from React modules.
 * KeyboardInput.tsx wraps this + clears its heldKeys Set.
 */
export function clearAllInputState(): void {
  inputState.left = false;
  inputState.right = false;
  inputState.down = false;
  inputState.up = false;
  inputState.jumpHeld = false;
  inputState.attackHeld = false;
  clearRisingEdgeFlags();
}
